// White balance (§6.3). temp/tint are relative to the image as shot; (0, 0) is the identity.
//
// DEVIATION (recorded in PROGRESS.md / REVIEW.md): the plan's formulas
//   T = 6500 / (1 + temp/100·0.6)   and   Δv = −tint/100·0.02
// contradict its own stated intent ("positive temp means a warmer result, so the assumed scene
// illuminant is cooler"; "positive tint means more magenta"). Adapting FROM a lower T to D65 cools the
// image. We keep the magnitudes and fix the signs so the stated intent holds:
//   T = 6500 / (1 − temp/100·0.6)  (±92 mired, symmetric)     Δuv = +tint/100·0.02 (toward green)
// Unit tests assert +temp raises R/B and +tint raises magenta on a neutral gray.
import { apply, lumaP3, mul, P3_TO_XYZ, scale, XYZ_TO_P3, type Mat3, type Vec3 } from './matrix';
import { bradford, planckWithOffset, planckXy, xyToXYZ } from './planckian';

export function tempToKelvin(temp: number): number {
  return Math.min(25000, Math.max(2000, 6500 / (1 - (temp / 100) * 0.6)));
}

/** The assumed scene illuminant for a (temp, tint) setting, as XYZ with Y = 1. */
export function assumedIlluminant(temp: number, tint: number): Vec3 {
  return xyToXYZ(...planckWithOffset(tempToKelvin(temp), (tint / 100) * 0.02));
}

const REF_WHITE: Vec3 = xyToXYZ(...planckXy(6500));

/** 3×3 matrix applied to linear working-space (P3) RGB. */
export function wbMatrix(temp: number, tint: number): Mat3 {
  if (temp === 0 && tint === 0)
    return [
      [1, 0, 0],
      [0, 1, 0],
      [0, 0, 1],
    ];
  const m = mul(XYZ_TO_P3, mul(bradford(assumedIlluminant(temp, tint), REF_WHITE), P3_TO_XYZ));
  // Preserve the luminance of white.
  const w = apply(m, [1, 1, 1]);
  return scale(m, 1 / lumaP3(w[0], w[1], w[2]));
}

/** Neutrality error: squared chroma of the balanced colour in log-ratio space (scale invariant). */
function neutralError(rgb: Vec3, temp: number, tint: number): number {
  const o = apply(wbMatrix(temp, tint), rgb).map((v) => Math.log(Math.max(v, 1e-9)));
  const m = (o[0]! + o[1]! + o[2]!) / 3;
  return (o[0]! - m) ** 2 + (o[1]! - m) ** 2 + (o[2]! - m) ** 2;
}

const clampSetting = (v: number) => Math.min(100, Math.max(-100, v));

/** 2D Nelder–Mead, minimal and deterministic. */
function nelderMead(
  f: (p: [number, number]) => number,
  start: [number, number],
  step: number,
  iters = 200,
): [number, number] {
  let s: { p: [number, number]; v: number }[] = [
    start,
    [start[0] + step, start[1]],
    [start[0], start[1] + step],
  ].map((p) => ({
    p: p as [number, number],
    v: f(p as [number, number]),
  }));
  for (let k = 0; k < iters; k++) {
    s.sort((a, b) => a.v - b.v);
    const [b, g, w] = s as [(typeof s)[0], (typeof s)[0], (typeof s)[0]];
    if (Math.abs(w.v - b.v) < 1e-14) break;
    const c: [number, number] = [(b.p[0] + g.p[0]) / 2, (b.p[1] + g.p[1]) / 2];
    const at = (t: number): [number, number] => [c[0] + t * (w.p[0] - c[0]), c[1] + t * (w.p[1] - c[1])];
    const r = at(-1);
    const fr = f(r);
    if (fr < b.v) {
      const e = at(-2);
      const fe = f(e);
      s = [b, g, fe < fr ? { p: e, v: fe } : { p: r, v: fr }];
    } else if (fr < g.v) s = [b, g, { p: r, v: fr }];
    else {
      const ct = at(fr < w.v ? -0.5 : 0.5);
      const fc = f(ct);
      if (fc < Math.min(fr, w.v)) s = [b, g, { p: ct, v: fc }];
      else
        s = [
          b,
          ...[g, w].map((q) => {
            const p: [number, number] = [b.p[0] + 0.5 * (q.p[0] - b.p[0]), b.p[1] + 0.5 * (q.p[1] - b.p[1])];
            return { p, v: f(p) };
          }),
        ];
    }
  }
  s.sort((a, b) => a.v - b.v);
  return s[0]!.p;
}

/** True when a sample is bright enough to say anything about colour (every channel finite, max ≥ 1e-4). */
export function canSolveWb(rgb: Vec3): boolean {
  return rgb.every((v) => Number.isFinite(v)) && Math.max(...rgb) >= 1e-4;
}

/**
 * Eyedropper / auto WB solver (§6.3): find (temp, tint) that make `rgb` (linear P3, after exposure,
 * before WB) neutral. Grid-search 41×41, then refine with Nelder–Mead, then clamp.
 */
export function solveWb(rgb: Vec3): { temp: number; tint: number } {
  // A black (or nearly black) sample carries no colour: every grid point scores the same and the first,
  // (−100, −100), used to win (CODE_REVIEW S12). Nothing to neutralize → identity.
  if (!canSolveWb(rgb)) return { temp: 0, tint: 0 };
  const f =(p: [number, number]) => neutralError(rgb, clampSetting(p[0]), clampSetting(p[1]));
  let best: [number, number] = [0, 0];
  let bestV = Infinity;
  for (let i = 0; i <= 40; i++)
    for (let j = 0; j <= 40; j++) {
      const p: [number, number] = [-100 + i * 5, -100 + j * 5];
      const v = f(p);
      if (v < bestV) {
        bestV = v;
        best = p;
      }
    }
  const p = nelderMead(f, best, 2.5);
  return { temp: clampSetting(p[0]), tint: clampSetting(p[1]) };
}
